/**
 * Performance calculation domain service
 * Contains business logic for runner performance metrics
 */

import { calculateTotalDistance, calculateElevationGain, haversineDistance, findLatestPositionByTime } from '../../utils/gpsUtils.js'
import { formatSecondsToTime, formatTimeFromNow, formatTime } from '../../utils/timeUtils.js'
import { formatDistance, formatSpeed } from '../../utils/unitUtils.js'

/**
 * Find the last track point with valid distance and progress fields
 * @param {Array} tracks - Array of track points (plain objects or TrackPoint instances)
 * @returns {Object|null} Last valid track point or null if none found
 */
function findLastValidTrack(tracks) {
  let latestTrack = null
  let latestTime = -Infinity

  for (const track of tracks) {
    const distance = track.distance || (track.hasDistance && track.distance)
    const progress = track.progress || (track.hasProgress && track.progress)
    const time = track.time && !isNaN(new Date(track.time).getTime()) ? new Date(track.time).getTime() : null

    if (
      distance !== undefined &&
      progress !== undefined &&
      distance > 0 &&
      progress > 0 &&
      time !== null
    ) {
      if (time > latestTime) {
        latestTime = time
        latestTrack = track
      }
    }
  }

  return latestTrack
}

/**
 * Calculate comprehensive performance metrics for a runner
 * @param {Array} tracks - GPS tracking points
 * @param {Array} splits - Checkpoint split times  
 * @returns {Object} Calculated performance values
 */
export function calculatePerformanceMetrics(tracks = [], splits = [], runner = null) {
  let totalTime = '0:00:00'
  let distance = '-- km'
  let averageSpeed = '-- km/h'
  let lastCheckpoint = '--'
  let nextCheckpoint = '--'
  let estimatedFinishTime = '--:--'
  let elevationGain = '-- m'
  let currentSpeed = '-- km/h'
  let currentAltitude = '-- m'
  let gpsBattery = '-- %'
  let lastTrackTime = '--'
  let formatedStartTime = '--:--'
  
  try {

    // Calculate formatted start time
    formatedStartTime = getDateTimeString(runner?.startTime)
    
    // Calculate total time and last checkpoint
    const timeInfo = calculateTimeInfo(splits, runner)
    totalTime = timeInfo.totalTime
    lastCheckpoint = timeInfo.lastCheckpoint
    
    // Calculate distance metrics
    const distanceInfo = calculateDistanceMetrics(tracks, timeInfo.totalSeconds)
    distance = distanceInfo.distance
    averageSpeed = distanceInfo.averageSpeed
    currentSpeed = distanceInfo.currentSpeed
    estimatedFinishTime = distanceInfo.estimatedFinishTime
    
    // Calculate altitude metrics
    const altitudeInfo = calculateAltitudeMetrics(tracks)
    currentAltitude = altitudeInfo.currentAltitude
    elevationGain = altitudeInfo.elevationGain
    
    // Calculate derived metrics
    nextCheckpoint = calculateNextCheckpoint(splits, tracks)
    gpsBattery = calculateGpsBattery(tracks)
    lastTrackTime = calculateLastTrackTime(tracks)
    
  } catch (error) {
    console.warn('Error calculating performance metrics:', error)
  }
  
  return {
    formatedStartTime,
    totalTime,
    distance,
    averageSpeed,
    lastCheckpoint,
    nextCheckpoint,
    estimatedFinishTime,
    elevationGain,
    currentSpeed,
    currentAltitude,
    gpsBattery,
    lastTrackTime
  }
}

// /**
//  * Calculate start time from tracks or splits
//  */
// function calculateStartTime(tracks, splits) {
//   if (tracks.length > 0 && tracks[0].time) {
//     return formatTime(tracks[0].time)
//   } else if (splits.length > 0 && splits[0].timeFromStartSeconds === 0) {
//     return formatTime(new Date())
//   }
//   return '--:--'
// }

/**
 * Calculate time information from splits
 */
function calculateTimeInfo(splits, runner) {
  let totalTime = '--:--'
  let lastCheckpoint = '--'
  let totalSeconds = 0

  if (runner && runner.startTime) {
    const startTimestamp = new Date(runner.startTime).getTime()
    const nowTimestamp = Date.now()
    const diffSeconds = Math.floor((nowTimestamp - startTimestamp) / 1000)
    if (diffSeconds >= 0) {
      totalSeconds = diffSeconds
      totalTime = formatSecondsToTime(totalSeconds)
    }
  }
console.log("splits", splits);
  if (splits.length > 0) {
    const latestSplit = splits[splits.length - 1]
    if (latestSplit.timeFromStartSeconds) {
      console.log("latestSplit: ", latestSplit);
      lastCheckpoint = latestSplit.name || '--'
      // Add time when checkpoint was reached
      if (latestSplit.time) {
        const checkpointTime = formatTime(latestSplit.time)
        lastCheckpoint = `${latestSplit.name || '--'} (${checkpointTime})`
      }
    }
  }

  return { totalTime, lastCheckpoint, totalSeconds }
}

/**
 * Calculate distance and speed metrics
 */
function calculateDistanceMetrics(tracks, totalSeconds) {
  let distance = '-- km'
  let averageSpeed = '-- km/h'
  let currentSpeed = '-- km/h'
  let estimatedFinishTime = '--:--'
  
  // First try to use distance and progress from track data
  const lastValidTrack = findLastValidTrack(tracks)
  
  if (lastValidTrack) {
    // Use distance from track data (assuming it's already in km for mock data)
    const trackDistance = lastValidTrack.distance / 1000.0 || 0
    distance = formatDistance(trackDistance)
    
    // Calculate average speed using track distance
    if (totalSeconds > 0 && trackDistance > 0) {
      const averageSpeedKmh = (trackDistance / totalSeconds) * 3600
      averageSpeed = formatSpeed(averageSpeedKmh)
      
      // Calculate estimated finish time using progress
      const progress = lastValidTrack.progress || 0
      if (progress > 0 && progress <= 100) {
        const totalRaceDistance = trackDistance * 100 / progress
        const remainingDistance = totalRaceDistance - trackDistance
        if (remainingDistance > 0) {
          const estimatedRemainingSeconds = (remainingDistance / averageSpeedKmh) * 3600
          const estimatedFinishDate = new Date(Date.now() + estimatedRemainingSeconds * 1000)
          estimatedFinishTime = formatTime(estimatedFinishDate)
        }
      }
    }
    
    // Calculate current speed from recent tracks
    currentSpeed = calculateCurrentSpeed(tracks)
  } else if (tracks.length > 1) {
    // Fallback to GPS coordinate-based calculation if no distance/progress data
    const distanceKm = calculateTotalDistance(tracks)
    distance = formatDistance(distanceKm)
    
    // Calculate average speed
    if (totalSeconds > 0 && distanceKm > 0) {
      const averageSpeedKmh = (distanceKm / totalSeconds) * 3600
      averageSpeed = formatSpeed(averageSpeedKmh)
      
      // Estimate finish time (rough calculation - without progress, use approximation)
      const totalRaceDistance = distanceKm * 1.5
      const remainingDistance = totalRaceDistance - distanceKm
      if (remainingDistance > 0) {
        const estimatedRemainingSeconds = (remainingDistance / averageSpeedKmh) * 3600
        const estimatedFinishDate = new Date(Date.now() + estimatedRemainingSeconds * 1000)
        estimatedFinishTime = formatTime(estimatedFinishDate)
      }
    }
    
    // Calculate current speed from recent tracks
    currentSpeed = calculateCurrentSpeed(tracks)
  }
  
  return { distance, averageSpeed, currentSpeed, estimatedFinishTime }
}

/**
 * Get the most recent tracks by time (not by array position)
 * @param {Array} tracks - Array of track points
 * @param {number} count - Number of recent tracks to return
 * @returns {Array} Array of recent tracks sorted by time
 */
function getRecentTracksByTime(tracks, count = 3) {
  if (!Array.isArray(tracks) || tracks.length === 0) {
    return []
  }
  
  // Filter out tracks without valid time data
  const tracksWithTime = tracks.filter(track => 
    track.time && !isNaN(new Date(track.time).getTime())
  )
  
  if (tracksWithTime.length === 0) {
    // Fallback to last tracks if no time data available
    return tracks.slice(-count)
  }
  
  // Sort by time and take the most recent ones
  return tracksWithTime
    .sort((a, b) => new Date(b.time) - new Date(a.time))
    .slice(0, count)
    .reverse() // Reverse to get chronological order for distance calculation
}

/**
 * Calculate current speed from recent tracking points
 */
function calculateCurrentSpeed(tracks) {
  if (tracks.length < 3) return '-- km/h'
  
  // Use time-based recent tracks instead of array index
  const recentTracks = getRecentTracksByTime(tracks, 3)
  if (recentTracks.length < 2) return '-- km/h'
  
  let recentDistance = 0
  let recentTime = 0
  
  for (let i = 1; i < recentTracks.length; i++) {
    const dist = haversineDistance(
      recentTracks[i - 1].latitude,
      recentTracks[i - 1].longitude,
      recentTracks[i].latitude,
      recentTracks[i].longitude
    )
    recentDistance += dist
    
    if (recentTracks[i].time && recentTracks[i - 1].time) {
      const timeDiff = (new Date(recentTracks[i].time) - new Date(recentTracks[i - 1].time)) / 1000
      recentTime += timeDiff
    }
  }
  
  if (recentTime > 0 && recentDistance > 0) {
    const currentSpeedKmh = (recentDistance / recentTime) * 3600
    return formatSpeed(currentSpeedKmh)
  }
  
  return '-- km/h'
}

/**
 * Calculate altitude metrics
 */
function calculateAltitudeMetrics(tracks) {
  let currentAltitude = '-- m'
  let elevationGain = '-- m'
  
  if (tracks.length > 0) {
    // Use time-based latest position instead of array index
    const latestTrack = findLatestPositionByTime(tracks)
    if (latestTrack && latestTrack.altitude !== undefined && latestTrack.altitude !== null) {
      currentAltitude = Math.round(latestTrack.altitude) + ' m'
    }
  }
  
  if (tracks.length > 1) {
    const elevationChange = calculateElevationGain(tracks)
    elevationGain = elevationChange > 0 ? `+${elevationChange} m` : `${elevationChange} m`
  }
  
  return { currentAltitude, elevationGain }
}

/**
 * Calculate next checkpoint information
 */
function calculateNextCheckpoint(splits, tracks = []) {
  if (splits.length === 0) {
    // If no checkpoints available, show generic next checkpoint info
    return 'META (dystans nieznany)'
  }
  
  // Find the most recent checkpoint
  const latestSplit = splits[splits.length - 1]
  
  // For a realistic ultramarathon, create logical next checkpoint
  if (!latestSplit || !latestSplit.name) {
    return 'Następny punkt kontrolny'
  }
  
  // Get current distance from tracks if available
  let currentDistanceKm = 0
  if (tracks.length > 0) {
    // Use time-based latest position instead of array index
    const lastTrack = findLatestPositionByTime(tracks)
    if (lastTrack && lastTrack.distance) {
      currentDistanceKm = lastTrack.distance / 1000.0
    }
  }
  
  // Based on current checkpoint, determine logical next checkpoint
  const checkpointName = latestSplit.name.toLowerCase()
  
  if (checkpointName.includes('start')) {
    const remainingKm = Math.max(0.5, 8.5 - currentDistanceKm)
    return `PZ Wijątru (${formatDistance(remainingKm)})`
  } else if (checkpointName.includes('wijątru') || checkpointName.includes('wiatr')) {
    const remainingKm = Math.max(0.5, 15.2 - currentDistanceKm)
    return `Wielka Sowa (${formatDistance(remainingKm)})`
  } else if (checkpointName.includes('sowa')) {
    const remainingKm = Math.max(0.5, 21.0 - currentDistanceKm)
    return `META (${formatDistance(remainingKm)})`
  } else {
    // Generic fallback for unknown checkpoints
    const estimatedRemainingKm = Math.max(1.0, 21.0 - currentDistanceKm)
    return `META (${formatDistance(estimatedRemainingKm)})`
  }
}

/**
 * Calculate GPS battery level (simulated)
 */
function calculateGpsBattery(tracks) {
  if (tracks.length === 0) return '-- %'
  
  // Use time-based latest position instead of array index
  const latestTrack = findLatestPositionByTime(tracks)
  if (latestTrack && latestTrack.time) {
    const trackAge = (Date.now() - new Date(latestTrack.time).getTime()) / 1000 / 60
    const batteryLevel = Math.max(15, Math.min(100, 100 - trackAge * 2))
    return Math.round(batteryLevel) + '%'
  }
  
  return '-- %'
}

/**
 * Calculate time since last track
 */
function calculateLastTrackTime(tracks) {
  if (tracks.length === 0) return '--'
  
  // Use time-based latest position instead of array index
  const latestTrack = findLatestPositionByTime(tracks)
  if (latestTrack && latestTrack.time) {
    return formatTimeFromNow(new Date(latestTrack.time))
  }
  
  return '--'
}

function getDateTimeString(dateTime) {
  if (!dateTime) return '--:--'
  const date = new Date(dateTime)
  return date.toLocaleTimeString('pl-PL', {
    hour: '2-digit',
    minute: '2-digit'
  })
}